import type { paths } from '@/types/api/v1'
import type { QueryClient, QueryFilters } from '@tanstack/react-query'
import { message } from 'antd'
import type { MaybeOptionalInit } from 'openapi-fetch'
import createFetchClient from 'openapi-fetch'
import createClient from 'openapi-react-query'
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers'
import { useTenantStore } from '../stores/tenantStore'
import { clearFullSessionStorage } from '../utils/sessionCleanup'
import { API_BASE_URL } from './config'

const getToken = () => localStorage.getItem('accessToken')

const fetchClient = createFetchClient<paths>({
  baseUrl: API_BASE_URL,
})

fetchClient.use({
  onRequest: ({ request }) => {
    const token = getToken()
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }

    // Only set tenant from store if not already provided in request headers
    // This allows callers to override the tenant (e.g., for Gravitate admin operations)
    if (!request.headers.has('X-Tenant-Name')) {
      const tenant = useTenantStore.getState().selectedTenant?.name
      if (tenant) {
        request.headers.set('X-Tenant-Name', tenant)
      }
    }

    request.headers.set('X-Selected-Object', 'terminal')

    return request
  },
  onResponse: async ({ response }) => {
    if (response.status === 401 || response.status === 403) {
      clearFullSessionStorage()
      window.location.href = '/login'
      return
    }

    if (!response.ok) {
      const body = response.clone()
      const errorData = await body.json().catch(() => null)

      if (errorData?.detail) {
        const details =
          typeof errorData.detail === 'string'
            ? [errorData.detail]
            : Array.isArray(errorData.detail)
              ? errorData.detail.map(
                  (d: { loc?: string[]; msg?: string }) =>
                    `${d.loc?.slice(1).join('.')}: ${d.msg}`,
                )
              : []

        if (details.length > 0) {
          console.error('API Error:', details.join(', '))
        }
      }
    }
  },
  onError: ({ error }) => {
    // AbortError occurs when requests are cancelled (e.g., component unmount,
    // query deduplication, navigation). This is expected behavior, not an error.
    if (error instanceof Error && error.name === 'AbortError') {
      return
    }

    console.error('Network or Fetch Error:', error)
    message.error('Something unexpected happened. Check console for details.')
  },
})

export const $api = createClient(fetchClient)

// Re-export the fetch client for direct use when needed
export { fetchClient }

// ---------------------------------------------------------------------------
// Utility types for inferring request/response types from endpoints
// ---------------------------------------------------------------------------

export type InferRequest<
  Path extends keyof paths,
  Method extends keyof paths[Path] = 'post',
> = paths[Path][Method] extends {
  requestBody: { content: { 'application/json': infer X } }
}
  ? X
  : never

export type InferResponse<
  Path extends keyof paths,
  Method extends keyof paths[Path] = 'post',
> = paths[Path][Method] extends {
  responses: { 200: { content: { 'application/json': infer X } } }
}
  ? X
  : never

export type InferFullRequest<
  M extends HttpMethod,
  Path extends PathsWithMethod<paths, M>,
> = MaybeOptionalInit<paths[Path], M>

// ---------------------------------------------------------------------------
// Query key utilities for cache management
// ---------------------------------------------------------------------------

type PathQueryFilters<T> = Omit<QueryFilters, 'predicate' | 'queryKey'> & {
  init?: T
}

const getQueryKeys = <
  M extends HttpMethod,
  Path extends PathsWithMethod<paths, M>,
>(
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>,
) =>
  init === undefined
    ? ([method, path] as const)
    : ([method, path, init] as const)

const getQueryFilters = <
  M extends HttpMethod,
  Path extends PathsWithMethod<paths, M>,
>(
  method: M,
  path: Path,
  options?: PathQueryFilters<MaybeOptionalInit<paths[Path], M>>,
) => {
  const queryKey = getQueryKeys(method, path, options?.init)
  return { ...options, queryKey }
}

// ---------------------------------------------------------------------------
// Typed query client helpers
// ---------------------------------------------------------------------------

export const invalidateTypedQueries = <
  M extends HttpMethod,
  Path extends PathsWithMethod<paths, M>,
>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>,
) => client.invalidateQueries(getQueryFilters(method, path, { init }))
